import { MouseEventHandler, ComponentType, ReactNode } from 'react';
import type { IconName } from '@ardoq/icons';
import { OnViewSettingsUpdate } from '@ardoq/data-model';
import type { DropdownItem, Notifier } from '@ardoq/dropdown-menu';
import type { ViewIds } from '@ardoq/api-types';
import { DropdownSize } from '@ardoq/dropdown-menu-ui';
import type { GraphComponent } from '@ardoq/yfiles';
import { KnowledgeBaseLink } from '@ardoq/knowledge-base';

export type SettingsConfig =
  | ToggleConfig
  | ButtonConfig
  | ExportConfig
  | DropdownConfig
  | CustomComponentConfig
  | DividerConfig;

interface SettingsConfigBase {
  id: string;
  type: SettingsType;
}

export interface ToggleConfig extends SettingsConfigBase {
  label?: string;
  isActive: boolean;
  iconName: IconName;
  persistent?: boolean;
  isInverted?: boolean;
  isDisabled?: boolean;
  currentValue?: boolean;
  popoverText?: string;
  onViewSettingsUpdate?: OnViewSettingsUpdate;
}

export interface ButtonConfig extends SettingsConfigBase {
  label: string;
  onClick: MouseEventHandler;
  iconName: IconName;
  isDisabled?: boolean;
  isActive?: boolean;
  rightAligned?: boolean;
  popoverText?: string;
  onViewSettingsUpdate?: OnViewSettingsUpdate;
  ariaLabel?: string;
}

export interface DropdownConfig extends SettingsConfigBase {
  label: string;
  iconName: IconName;
  /**
   * When set, the dropdown renders as a text combo showing this text (typically
   * the active option) with a chevron, instead of the icon-only button. `label`
   * stays the accessible name / tooltip.
   */
  buttonText?: string;
  options: DropdownItem[];
  isKeepOpen?: boolean;
  isActive?: boolean;
  isDisabled?: boolean;
  rightAligned?: boolean;
  className?: string;
  clickNamespace?: string;
  popoverText?: string;
  popoverContent?: ReactNode;
  requestOpen?: Notifier;
  onOpened?: VoidFunction;
  dropdownSize?: DropdownSize;
  maxDropdownSize?: DropdownSize;
  maxDropdownHeight?: number;
}

export interface ExportConfigParams {
  exportToPng?: MouseEventHandler;
  exportToSvg?: MouseEventHandler;
  exportToLucidchart?: MouseEventHandler;
  addToPresentation?: MouseEventHandler;
  printOrSaveToPdf?: MouseEventHandler;
  isDisabled?: boolean;
  disabledMessage?: string;
}

export interface ExportConfig extends ExportConfigParams, SettingsConfigBase {
  label: string;
}

export interface CustomComponentConfig extends SettingsConfigBase {
  component: ComponentType;
  componentProps?: Record<string, any>;
  /**
   * What names the row in the settings panel.
   *
   * Optional, but a control the reader can see wants one: `ViewSettingsRow` falls back to `id`,
   * so a config without it puts a camelCase identifier where every sibling row has a name.
   */
  label?: string;
  iconName?: IconName;
}

export type DividerConfig = SettingsConfigBase & { type: SettingsType.DIVIDER };

export type CreateDegreesOfRelationshipSlider<T> = (
  viewSettings: T,
  onViewSettingsUpdate: OnViewSettingsUpdate,
  isFlexible: boolean,
  VIEW_ID: ViewIds
) => DropdownConfig | null;

type ExportCanvas = HTMLCanvasElement | OffscreenCanvas | null;

export type ViewContext = {
  /** @returns the container used to export the view to an image. */
  getContainer: () => HTMLElement | null;
  /** @returns the canvas to export to an image, or a promise for one the view has to render first. */
  createExportCanvas?: () => ExportCanvas | Promise<ExportCanvas>;
  getGraphComponent: () => GraphComponent | null;
  toggleLegend: VoidFunction;
  zoomIn: VoidFunction | null;
  zoomOut: VoidFunction | null;
  optimizeLayout: VoidFunction | null;
  /** set this to a non-null value to explicitly provide all zoom controls from the view. these appear in the view pane header right controls. */
  zoomControls: SettingsConfig[] | null;
  zoomControlsDisabledState: DisabledZoomControls;
  isLegendActive: boolean;
  viewInstanceId?: string;
  knowledgeBaseLink?: KnowledgeBaseLink;
};

export enum SettingsType {
  TOGGLE = 'SETTINGS_TOGGLE',
  BUTTON = 'SETTINGS_BUTTON',
  EXPORT = 'SETTINGS_EXPORT',
  DROPDOWN = 'SETTINGS_DROPDOWN',
  CUSTOM_COMPONENT = 'SETTINGS_CUSTOM_COMPONENT',
  DIVIDER = 'SETTINGS_DIVIDER',
}

export enum DisabledZoomControls {
  NONE = 0,
  FIT_WINDOW = 1,
  ZOOM_IN = 2,
  ZOOM_OUT = 4,
}

export type ViewSettingsControlsDependencies<T> = {
  /**
   * Views that publish plain dependencies leave this out; the settings bar
   * composes their controls from those dependencies instead.
   */
  viewModifiers?: SettingsConfig[];
  viewContext: ViewContext;
  isEmptyView: boolean;
  viewSettings: T;
  viewInstanceId: string;
};

export type ViewProperties<T> = {
  viewModifiers: SettingsConfig[];
  viewContext: ViewContext;
  viewSettings: T;
};
